import React from 'react'
import { Link } from 'react-router-dom'
import { Scale, FileCheck, Shield, CheckCircle, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const CompliancePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Scale className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-6xl mb-6">
              Compliance & Regulatory
            </h1>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              We ensure full compliance with all IRS regulations and industry standards for 1031 exchanges.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance Standards */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* IRS Compliance */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <FileCheck className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                IRS Compliance
              </h3>
              <p className="text-muted-foreground">
                Full compliance with IRS Section 1031 regulations, including Form 8824 filing and reporting requirements.
              </p>
            </div>

            {/* KYC/AML */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                KYC/AML
              </h3>
              <p className="text-muted-foreground">
                Know Your Customer and Anti-Money Laundering compliance for all transactions and participants.
              </p>
            </div>

            {/* State Regulations */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                State Regulations
              </h3>
              <p className="text-muted-foreground">
                Compliance with state-specific regulations and requirements across all 50 states.
              </p>
            </div>

            {/* Qualified Intermediary */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Scale className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Qualified Intermediary
              </h3>
              <p className="text-muted-foreground">
                Licensed and bonded qualified intermediary services meeting all regulatory requirements.
              </p>
            </div>

            {/* Data Protection */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Data Protection
              </h3>
              <p className="text-muted-foreground">
                GDPR, CCPA, and other data protection regulation compliance for customer information.
              </p>
            </div>

            {/* Regular Audits */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Regular Audits
              </h3>
              <p className="text-muted-foreground">
                Third-party compliance audits and internal reviews to ensure ongoing regulatory adherence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 bg-muted/50 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Certifications & Standards
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">SOC 2 Type II</h3>
                <p className="text-muted-foreground">
                  Certified for security, availability, processing integrity, confidentiality, and privacy controls.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">IRS Qualified</h3>
                <p className="text-muted-foreground">
                  Licensed qualified intermediary meeting all IRS requirements for 1031 exchange facilitation.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">GDPR Compliant</h3>
                <p className="text-muted-foreground">
                  Full compliance with European General Data Protection Regulation requirements.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">CCPA Compliant</h3>
                <p className="text-muted-foreground">
                  California Consumer Privacy Act compliance for California residents.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl mb-4">
            Questions About Compliance?
          </h2>
          <p className="mt-4 text-lg text-primary-foreground/90 mb-8">
            Our compliance team is here to answer any questions about regulatory requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                Contact Compliance Team
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/privacy">
              <Button variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                View Privacy Policy
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

